import { Box, Button, TextField } from "@radix-ui/themes";
import FontAwesome from "../../utilities/FontAwesome";
import React, { useCallback, useEffect, useRef } from "react";
import WatsonApi from "../../../backends/WatsonApi";
import { MessagingListQueryOptions } from "./MessagingListRules";
import { IMessageListRuleTag } from "./MessagingListRuleQueryBuilder";
import { useStores } from "../../../stores/useStores";
import { IMessagingListModalIntent } from "./AddOrEditMessagingListModal";

export function RuleOrganizationSearch(props: {
  setOrganizations: React.Dispatch<React.SetStateAction<IMessageListRuleTag[]>>;
  modalIntent: IMessagingListModalIntent;
  searchValue: string;
  setSearchValue: React.Dispatch<React.SetStateAction<string>>;
  setSearchResultsLoading: React.Dispatch<React.SetStateAction<boolean>>;
  searchResultsLoading: boolean;
  resetSearch: () => void;
}) {
  const searchTimeOutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const { organizationStore } = useStores();

  useEffect(() => {
    return () => {
      if (searchTimeOutRef.current) clearTimeout(searchTimeOutRef.current);
    };
  }, []);

  const handleInputChange = useCallback(
    (e) => {
      e.preventDefault();
      e.stopPropagation();
      if (searchTimeOutRef.current) clearTimeout(searchTimeOutRef.current);
      props.setSearchValue(e.target.value);

      if (!e.target.value) return;
      props.setSearchResultsLoading(true);
      searchTimeOutRef.current = setTimeout(async () => {
        try {
          const allowedTypes = ["school", "district", "classroom"];
          if (
            !allowedTypes.includes(organizationStore.currentOrganization.type)
          ) {
            // make sure that the current org's type is included so can add current page to rules
            allowedTypes.push(organizationStore.currentOrganization.type);
            if (!allowedTypes.includes("section")) {
              // if we're here it means this is a subpage, so make sure sections are included so sub-subpages are allowed
              allowedTypes.push("section");
            }
          }

          const client = await WatsonApi();
          const response = await client.apis.organizations.organizations_list({
            type__in: allowedTypes,
            fields: ["title", "type", "id"],
            search: e.target.value,
            deleted: "false",
            ordering: "title",
            page_size: 12,
            path__descendant_or_eq: organizationStore.currentOrganization.path,
            with_role: props.modalIntent === "createMessage" ? true : undefined,
          });
          const results = JSON.parse(response.data).data;
          const options = results.map((org) => ({
            value: `${org.title} (${org.type})`,
            type: MessagingListQueryOptions.Organization,
            filter: org.id,
          }));
          props.setOrganizations(options);
        } catch (error) {
          console.error("Error fetching organizations:", error);
        } finally {
          props.setSearchResultsLoading(false);
        }
      }, 250);
    },
    [organizationStore.currentOrganization.path],
  );

  return (
    <Box pb={"1"}>
      <TextField.Root
        onKeyDown={(e) => {
          // prevent events from bubbling up to Select
          e.stopPropagation();
        }}
        onChange={handleInputChange}
        placeholder={"Search"}
        value={props.searchValue}
      >
        <TextField.Slot side={"left"}>
          {props.searchResultsLoading ? (
            <FontAwesome name={"fa-solid fa-spinner"} spin />
          ) : (
            <FontAwesome name={"fa-magnifying-glass"} prefix={"fas"} />
          )}
        </TextField.Slot>
        <TextField.Slot side={"right"}>
          <Button
            variant={"ghost"}
            size={"1"}
            color={"gray"}
            onClick={props.resetSearch}
          >
            <FontAwesome prefix={"fas"} name={"fa-times"} />
          </Button>
        </TextField.Slot>
      </TextField.Root>
    </Box>
  );
}
